import { Injectable, inject } from '@angular/core';
import { ConsoleLog } from 'src/app/user-identification/models/user-identification';
import { CloudLogsService, LogType } from './cloud-logs.service';

export type ConsoleCaptureSource = 'console.error' | 'console.warn' | 'window.error' | 'unhandledrejection';

export interface IConsoleCaptureEntry {
	timestamp: string;
	source: ConsoleCaptureSource;
	message: string;
	stack?: string;
	url: string;
	userAgent: string;
}

const MAX_QUEUE_SIZE = 40;
const FLUSH_BATCH_SIZE = 12;
const FLUSH_INTERVAL_MS = 3000;
const MAX_MESSAGE_LENGTH = 4000;
const MAX_STACK_LENGTH = 8000;
const DEDUP_WINDOW_MS = 5000;

/** Patrones ignorados para evitar bucles o ruido del propio envío de logs. */
const IGNORED_MESSAGE_PATTERNS = [/^\s*Log guardado\s*$/i, /^\s*Error log\s*$/i];

@Injectable({
	providedIn: 'root',
})
export class ConsoleCaptureService {
	private cloudLogsService = inject(CloudLogsService);

	private installed = false;
	private forwarding = false;
	private flushTimer: ReturnType<typeof setTimeout> | null = null;

	private queue: IConsoleCaptureEntry[] = [];
	private recentFingerprints = new Map<string, number>();

	private originalConsoleError: typeof console.error | null = null;
	private originalConsoleWarn: typeof console.warn | null = null;

	private readonly onWindowError = (event: ErrorEvent): void => {
		const message = event.message || 'Script error';
		const stack = event.error?.stack ?? `${event.filename ?? ''}:${event.lineno ?? 0}:${event.colno ?? 0}`;
		this.capture('window.error', message, stack, ConsoleLog.UNHANDLED_ERROR);
	};

	private readonly onUnhandledRejection = (event: PromiseRejectionEvent): void => {
		const { message, stack } = this.serializeRejection(event.reason);
		this.capture('unhandledrejection', message, stack, ConsoleLog.UNHANDLED_REJECTION);
	};

	/** Registra interceptores de consola y errores globales (idempotente). */
	public install(): void {
		if (this.installed || typeof window === 'undefined') {
			return;
		}
		this.installed = true;

		this.originalConsoleError = console.error.bind(console);
		this.originalConsoleWarn = console.warn.bind(console);

		console.error = (...args: unknown[]) => {
			this.originalConsoleError?.(...args);
			this.handleConsoleCall('console.error', args, ConsoleLog.ERROR, LogType.Error);
		};

		console.warn = (...args: unknown[]) => {
			this.originalConsoleWarn?.(...args);
			this.handleConsoleCall('console.warn', args, ConsoleLog.WARNING, LogType.Warning);
		};

		window.addEventListener('error', this.onWindowError);
		window.addEventListener('unhandledrejection', this.onUnhandledRejection);

		window.addEventListener('pagehide', () => this.flush());
	}

	public enqueue(entry: Omit<IConsoleCaptureEntry, 'url' | 'userAgent' | 'timestamp'> & Partial<IConsoleCaptureEntry>): void {
		this.capture(entry.source, entry.message, entry.stack, this.infoForSource(entry.source));
	}

	private handleConsoleCall(
		source: 'console.error' | 'console.warn',
		args: unknown[],
		info: ConsoleLog,
		logType: LogType,
	): void {
		if (this.forwarding) {
			return;
		}
		const { message, stack } = this.serializeConsoleArgs(args);
		if (this.shouldIgnore(message)) {
			return;
		}
		this.pushEntry({ source, message, stack }, info, logType);
	}

	private capture(
		source: ConsoleCaptureSource,
		message: string,
		stack: string | undefined,
		info: ConsoleLog,
	): void {
		if (this.forwarding || this.shouldIgnore(message)) {
			return;
		}
		const logType =
			source === 'console.warn'
				? LogType.Warning
				: LogType.Error;
		this.pushEntry({ source, message, stack }, info, logType);
	}

	private pushEntry(
		partial: Pick<IConsoleCaptureEntry, 'source' | 'message' | 'stack'>,
		_info: ConsoleLog,
		_logType: LogType,
	): void {
		const message = this.truncate(partial.message, MAX_MESSAGE_LENGTH);
		if (this.isDuplicate(message, partial.stack, partial.source)) {
			return;
		}

		const entry: IConsoleCaptureEntry = {
			timestamp: new Date().toISOString(),
			source: partial.source,
			message,
			stack: partial.stack ? this.truncate(partial.stack, MAX_STACK_LENGTH) : undefined,
			url: window.location.href,
			userAgent: navigator.userAgent,
		};

		if (this.queue.length >= MAX_QUEUE_SIZE) {
			this.queue.shift();
		}
		this.queue.push(entry);

		if (this.queue.length >= FLUSH_BATCH_SIZE) {
			this.flush();
			return;
		}
		this.scheduleFlush();
	}

	private scheduleFlush(): void {
		if (this.flushTimer !== null) {
			return;
		}
		this.flushTimer = setTimeout(() => {
			this.flushTimer = null;
			this.flush();
		}, FLUSH_INTERVAL_MS);
	}

	/** Envía la cola acumulada al endpoint `/log` (vía CloudLogsService). */
	public flush(): void {
		if (this.queue.length === 0) {
			return;
		}

		const batch = this.queue.splice(0, FLUSH_BATCH_SIZE);
		const hasError = batch.some((e) => e.source !== 'console.warn');
		const logType = hasError ? LogType.Error : LogType.Warning;

		this.forwarding = true;
		try {
			this.cloudLogsService.sendCloudLogs(
				{ entries: batch },
				ConsoleLog.BATCH,
				logType,
			);
		} finally {
			this.forwarding = false;
		}

		if (this.queue.length > 0) {
			this.scheduleFlush();
		}
	}

	private infoForSource(source: ConsoleCaptureSource): ConsoleLog {
		switch (source) {
			case 'console.warn':
				return ConsoleLog.WARNING;
			case 'unhandledrejection':
				return ConsoleLog.UNHANDLED_REJECTION;
			case 'window.error':
				return ConsoleLog.UNHANDLED_ERROR;
			default:
				return ConsoleLog.ERROR;
		}
	}

	private serializeConsoleArgs(args: unknown[]): { message: string; stack?: string } {
		if (args.length === 0) {
			return { message: '(empty console call)' };
		}
		const parts = args.map((arg) => this.serializeValue(arg));
		const firstError = args.find((arg): arg is Error => arg instanceof Error);
		return {
			message: parts.join(' '),
			stack: firstError?.stack,
		};
	}

	private serializeRejection(reason: unknown): { message: string; stack?: string } {
		if (reason instanceof Error) {
			return { message: reason.message || reason.name, stack: reason.stack };
		}
		return { message: this.serializeValue(reason) };
	}

	private serializeValue(value: unknown): string {
		if (value === null) {
			return 'null';
		}
		if (value === undefined) {
			return 'undefined';
		}
		if (value instanceof Error) {
			return value.message || value.name;
		}
		if (typeof value === 'string') {
			return this.redactSensitive(value);
		}
		if (typeof value === 'object') {
			try {
				return this.redactSensitive(JSON.stringify(value));
			} catch {
				return '[Unserializable object]';
			}
		}
		return this.redactSensitive(String(value));
	}

	private redactSensitive(text: string): string {
		return text
			.replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Bearer [REDACTED]')
			.replace(/(authorization["']?\s*[:=]\s*["']?)[^"'\s]+/gi, '$1[REDACTED]');
	}

	private shouldIgnore(message: string): boolean {
		return IGNORED_MESSAGE_PATTERNS.some((pattern) => pattern.test(message.trim()));
	}

	private isDuplicate(message: string, stack: string | undefined, source: ConsoleCaptureSource): boolean {
		const fingerprint = `${source}|${message}|${stack ?? ''}`;
		const now = Date.now();
		const last = this.recentFingerprints.get(fingerprint);
		if (last !== undefined && now - last < DEDUP_WINDOW_MS) {
			return true;
		}
		this.recentFingerprints.set(fingerprint, now);
		if (this.recentFingerprints.size > 200) {
			const cutoff = now - DEDUP_WINDOW_MS;
			for (const [key, ts] of this.recentFingerprints) {
				if (ts < cutoff) {
					this.recentFingerprints.delete(key);
				}
			}
		}
		return false;
	}

	private truncate(value: string, max: number): string {
		if (value.length <= max) {
			return value;
		}
		return `${value.slice(0, max)}…[truncated]`;
	}
}
